'use client'

import Text from '@shared/components/Text/Text'
import { useStore } from '@shared/lib/store'
import { deterministicUrlify } from '@shared/lib/utils'
import Link from 'next/link'
import {
  createContext,
  type CSSProperties,
  type FunctionComponent,
  memo,
  type MutableRefObject,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { observe } from 'react-intersection-observer'
import {
  handleClauseVisibilityChange,
  highlightedClauseStore,
} from '../Clause/scroll-store'
import * as styles from './Sidebar.css'
import { scrollHintStore } from './Sidebar.stores'

export const TocContext = createContext<{
  ref: MutableRefObject<HTMLDivElement | null>
  onSelect?: () => void
}>({ ref: { current: null } })

/** Single ToC node produced by `TocServer`. Tree is depth-first, children-only on groups. */
export interface TocNode {
  type: 'group' | 'clause'
  enumeration?: string
  title?: string
  href: string
  /** Sequential clause index that lines up with `useRegisterSidebarHighlighting`. */
  clauseIndex?: number
  children?: TocNode[]
}

interface TocProps {
  tree: TocNode[]
  totalClauses: number
}

/** Re-export so callers can keep `import { scrollHintStore } from './Toc'`. */
export { scrollHintStore }

/**
 * Hierarchical Table of Contents for PG-backed laws.
 *
 * - Top-level entries are numbered chapters; nested entries indent per level
 *   (supports 4–5 deep without horizontal overflow because indent decays).
 * - The branch that contains the currently highlighted clause auto-expands,
 *   the active leaf shows a leading dot and brand-colored text.
 * - User can manually collapse/expand any branch via the chevron; their choice
 *   sticks until the highlighted clause moves to a different top-level branch.
 */
export default function Toc({ tree, totalClauses }: TocProps) {
  const { onSelect } = useContext(TocContext)
  const highlightedClause = useStore(highlightedClauseStore)

  // Compute the active path once per highlight change so each branch resolves
  // its highlight state via an O(1) Set lookup (see `buildActivePath`).
  const { path: activePath, active: activeNode } = useMemo(
    () => buildActivePath(tree, highlightedClause),
    [tree, highlightedClause],
  )

  const progress =
    totalClauses > 1
      ? Math.min(
          100,
          Math.max(
            0,
            Math.round((highlightedClause / (totalClauses - 1)) * 100),
          ),
        )
      : 0

  if (tree.length === 0) {
    return (
      <Text
        style={{ fontStyle: 'italic', textAlign: 'center', paddingTop: '1em' }}
        color='secondary'
      >
        Dieses Dokument hat keine Paragrafen
      </Text>
    )
  }

  return (
    <div className={styles.tree}>
      {totalClauses > 0 && <ReadingProgress progress={progress} />}
      <div className={styles.branchList}>
        {tree.map((node, i) => (
          <TocBranch
            key={i}
            node={node}
            number={i + 1}
            level={1}
            activePath={activePath}
            activeNode={activeNode}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

function ReadingProgress({ progress }: { progress: number }) {
  return (
    <div className={styles.progressWrapper}>
      <div className={styles.progressLabelRow}>
        <span className={styles.progressLabel}>Lesefortschritt</span>
        <span className={styles.progressValue}>{progress}%</span>
      </div>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

interface BranchProps {
  node: TocNode
  number?: number
  level: number
  /** Ancestor refs of the active clause; membership = "contains active". */
  activePath: ReadonlySet<TocNode>
  /** The active clause node itself, or null when nothing is highlighted. */
  activeNode: TocNode | null
  onSelect?: () => void
}

const TocBranch = memo(function TocBranch({
  node,
  number,
  level,
  activePath,
  activeNode,
  onSelect,
}: BranchProps) {
  const containsActive = activePath.has(node)
  const isActive = node === activeNode
  const hasChildren = (node.children?.length ?? 0) > 0

  // null = follow `containsActive`. true/false = user override. Override is
  // cleared when the active clause re-enters this branch so navigation Just
  // Works: clicking a clause re-expands its ancestors.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null)
  const expanded = userExpanded ?? containsActive
  useEffect(() => {
    if (containsActive) setUserExpanded(null)
  }, [containsActive])

  const toggle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setUserExpanded((prev) => !(prev ?? containsActive))
    },
    [containsActive],
  )

  const label = [node.enumeration, node.title].filter(Boolean).join(' \u2014 ')
  const isTopLevel = level === 1
  const rowClass = isTopLevel ? styles.chapterRow : styles.subRow
  // Indent caps at level 5 so deep nesting doesn't push labels off-screen.
  const indent = Math.min(level - 1, 4) * 14

  return (
    <div className={styles.branch}>
      <div
        className={`${rowClass}${containsActive ? ` ${styles.rowActive}` : ''}`}
        style={{ paddingLeft: 8 + indent }}
      >
        {isTopLevel && number !== undefined && (
          <span className={styles.chapterNumber}>{number}</span>
        )}
        {!isTopLevel && (
          <span
            className={`${styles.subBullet}${isActive ? ` ${styles.subBulletActive}` : ''}`}
            aria-hidden
          />
        )}
        <Link
          className={`${styles.rowLabel}${isActive ? ` ${styles.rowLabelActive}` : ''}`}
          href={node.href}
          onClick={() => onSelect?.()}
          prefetch={false}
          title={label || undefined}
        >
          <Text maxLines={1} weight={isTopLevel ? 500 : 400} size='0.9em'>
            {label}
          </Text>
        </Link>
        {hasChildren && (
          <button
            type='button'
            className={styles.chevron}
            onClick={toggle}
            aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
      </div>
      {expanded && hasChildren && node.children && (
        <div className={styles.branchChildren}>
          {node.children.map((child, i) => (
            <TocBranch
              key={i}
              node={child}
              level={level + 1}
              activePath={activePath}
              activeNode={activeNode}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
})

/**
 * Single depth-first walk collecting every node on the path to the active
 * clause into a Set (each ancestor + the clause itself), plus the active node.
 */
function buildActivePath(
  tree: TocNode[],
  target: number,
): { path: Set<TocNode>; active: TocNode | null } {
  const path = new Set<TocNode>()
  let active: TocNode | null = null

  const walk = (node: TocNode): boolean => {
    let onPath = false
    if (node.type === 'clause' && node.clauseIndex === target) {
      active = node
      onPath = true
    }
    if (node.children) {
      for (const child of node.children) {
        if (walk(child)) onPath = true
      }
    }
    if (onPath) path.add(node)
    return onPath
  }

  for (const node of tree) walk(node)
  return { path, active }
}

/* ------------------------------------------------------------------ */
/*  Backward-compatible exports used by decisions, BottomBar, Clause.  */
/* ------------------------------------------------------------------ */

/**
 * Register a content section's ref so the sidebar can reflect "currently
 * visible" state via the global `highlightedClauseStore`.
 * Each registered item should map 1-to-1 to a `clauseIndex` in the ToC tree.
 */
export function useRegisterSidebarHighlighting(
  ref: RefObject<HTMLDivElement | null>,
  index,
) {
  useEffect(() => {
    const elem = ref.current
    if (elem === null) return
    function onChange(inView) {
      handleClauseVisibilityChange({ inView, index })
    }
    return observe(elem, onChange, { rootMargin: '-135px 0px 0px 0px' })
  }, [ref, index])
}

interface TocItemProps {
  isHighlighted: boolean
  title?: string
  enumeration?: string
  onSelect?: () => void
  offset?: number
  style?: CSSProperties
  href?: string
  enumerationHash?: string
}

/**
 * Legacy single-row link used by decision pages and EU heading lists.
 * Kept here so existing imports (`import { TocItem } from './Sidebar/Toc'`)
 * keep working — the PG ToC uses its own `TocBranch` rendering above.
 */
export const TocItem: FunctionComponent<TocItemProps> = memo(function TocItem({
  title,
  enumeration,
  isHighlighted,
  onSelect,
  offset,
  style = {},
  href,
}) {
  return (
    <Link
      className={styles.tocItem}
      onClick={() => {
        onSelect?.()
      }}
      style={{
        // Use `top` instead of transform so the ":active" scale effect still works.
        top: offset !== undefined ? offset - 40 : undefined,
        ...style,
      }}
      // `deterministicUrlify` fallback retained for legacy callers that rely on it.
      href={href ?? `#${deterministicUrlify(enumeration ?? title ?? '')}`}
      prefetch={false}
    >
      {enumeration && (
        <span className={styles.tocItemEnumeration}>{enumeration}</span>
      )}
      <Text
        className={`${styles.tocItemText}${isHighlighted ? ' highlighted' : ''}`}
        maxLines={1}
        style={{ marginLeft: !enumeration ? -6 : undefined }}
      >
        {title}
        <HighlightingEffect isHighlighted={isHighlighted} />
      </Text>
    </Link>
  )
})

const HighlightingEffect = memo(function HighlightingEffect({
  isHighlighted,
}: {
  isHighlighted: boolean
}) {
  return (
    <span
      className={`${styles.tocItemHighlightEffect} ${isHighlighted ? 'highlighted' : ''}`}
    />
  )
})
